//@ts-check
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { HiHome } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';

import useUser from '@datahooks/useUser';
import { useProjectsListContext } from '@datahooks/ProjectsListContext';
import { useOrgContext } from '@datahooks/admin/organisation/AdminOrgIDContext';
import useOrganisationOverview from '@datahooks/useOrganisationOverview';

import AuthenticatedLayout from '../layouts/AuthenticatedLayout';
import { TestableText, TestableCard, TestableButton } from '@components/general/TestableComponents';
import OrganisationOverviewKpiSection from '@components/cards/overview/organisation/OrganisationOverviewKpiSection';
import OrganisationOverviewHealthMatrix from '@components/cards/overview/organisation/OrganisationOverviewHealthMatrix';
import OrganisationOverviewProjectTable from '@components/cards/overview/organisation/OrganisationOverviewProjectTable';
import { ErrorCard, CARD_TYPES } from '@components/cards/ErrorCard';
import AvatarLogo from '@components/general/AvatarLogo';
import AIExplainerButton, { useOpenExplainer } from '@components/cards/overview/organisation/AIExplainerButton';
import SwitchOrganisationDialog from '@components/cards/overview/organisation/SwitchOrganisationDialog';
import { ENABLE_AI_EXPLAINER } from '../config';

/**
 * Handles /overview/explainer and /overview/explainer/:orgId. Decides once, after the user, organisation
 * and projects have loaded: the panel freezes its props when opened, and the branches compare org ids.
 *
 * With an org id there are three outcomes. It is the active organisation, so open. The user is a super
 * admin, so ask before switching the whole dashboard. Anyone else gets their own organisation with a
 * note. The context is only touched after the dialog has confirmed the organisation exists, so a bad id
 * never reaches localStorage. The URL is left alone when the panel opens, since it is the link and a
 * refresh simply re-runs it; declining the switch replaces it with the plain overview.
 *
 * @param {{ active: boolean, orgId?: string }} args - active is whether the route is an explainer link.
 */
function useExplainerLink({ active, orgId }) {
  const { organisationData, changeOrganisation } = useOrgContext();
  const { projectsWithTags, isLoading: projectsLoading } = useProjectsListContext();
  const { user, isLoading: userLoading } = useUser();
  const navigate = useNavigate();
  const openExplainer = useOpenExplainer();

  const [pendingOrgId, setPendingOrgId] = useState(/** @type {number|null} */ (null)); // The dialog is up for this org
  const [awaited, setAwaited] = useState(/** @type {{ orgId: number, fromOrgId: number|undefined }|null} */ (null)); // Switch confirmed; open once the context has it
  const leftOrigin = useRef(false); // Whether the active org has moved off awaited.fromOrgId since the switch
  const handled = useRef(false);

  const activeOrgId = organisationData?.id;
  const organisationName = organisationData?.title ?? '';
  const loaded = !userLoading && Boolean(user) && Boolean(activeOrgId) && !projectsLoading;

  const open = useCallback((options = {}) => {
    openExplainer({ organisationName, projects: projectsWithTags, ...options });
  }, [openExplainer, organisationName, projectsWithTags]);

  // A different link, or the same link re-entered after leaving the page, is a new decision.
  useEffect(() => {
    handled.current = false;
  }, [active, orgId]);

  useEffect(() => {
    if (!active || handled.current || !loaded) return;
    handled.current = true;

    if (!ENABLE_AI_EXPLAINER) return;

    // No org in the link: open as the button would.
    if (orgId === undefined) {
      open();
      return;
    }

    const linkedOrgId = Number(orgId);
    const isValidId = Number.isInteger(linkedOrgId) && linkedOrgId > 0;

    if (isValidId && linkedOrgId === activeOrgId) {
      open();
    } else if (isValidId && user?.user_type === 'super_admin') {
      setPendingOrgId(linkedOrgId);
    } else {
      open({ linkedOrgUnavailable: true });
    }
  }, [active, loaded, orgId, activeOrgId, user, open]);

  // After a confirmed switch, open once the context and projects have caught up. If the context rolls the
  // switch back, the active org returns to where it started after having left it; stop waiting then, or a
  // later visit to that organisation would open the panel out of nowhere.
  useEffect(() => {
    if (awaited === null) return;

    if (activeOrgId !== awaited.fromOrgId) leftOrigin.current = true;

    if (activeOrgId === awaited.orgId && !projectsLoading) {
      setAwaited(null);
      open();
    } else if (leftOrigin.current && activeOrgId === awaited.fromOrgId) {
      setAwaited(null);
    }
  }, [awaited, activeOrgId, projectsLoading, open]);

  const confirmSwitch = useCallback((/** @type {{ id: number }} */ organisation) => {
    setPendingOrgId(null);
    leftOrigin.current = false;
    setAwaited({ orgId: organisation.id, fromOrgId: activeOrgId });
    changeOrganisation(organisation.id);
  }, [changeOrganisation, activeOrgId]);

  // Declined, so the URL should stop claiming the link. No panel is open here, so navigating is safe.
  const cancelSwitch = useCallback(() => {
    setPendingOrgId(null);
    navigate('/overview', { replace: true });
  }, [navigate]);

  return { pendingOrgId, organisationName, confirmSwitch, cancelSwitch };
}

export default function OrganisationOverview({ openExplainer = false }) {
  return (
    <AuthenticatedLayout>
      <OrganisationOverviewContent openExplainer={openExplainer} />
    </AuthenticatedLayout>
  );
}

OrganisationOverview.propTypes = {
  openExplainer: PropTypes.bool,
};

function OrganisationOverviewContent({ openExplainer = false }) {
  const { organisationData } = useOrgContext();
  const { projectsWithTags, filteredFlatProjects, filters, isLoading: projectsLoading } = useProjectsListContext();
  const { orgOverview, isLoading: orgOverviewLoading, error } = useOrganisationOverview();
  const { user: userDetails } = useUser();
  const navigate = useNavigate();
  const { orgId } = useParams();

  const { pendingOrgId, organisationName, confirmSwitch, cancelSwitch } = useExplainerLink({ active: openExplainer, orgId });

  const switchDialog = pendingOrgId === null ? null : (
    <SwitchOrganisationDialog
      orgId={pendingOrgId}
      currentOrganisationName={organisationName}
      onConfirm={confirmSwitch}
      onCancel={cancelSwitch}
    />
  );

  if (error) {
    return (
      <>
        <TestableCard dataTestId="org-overview-error-card" className="p-4 mt-5 w-full text-center text-blue-500">
          <ErrorCard cardType={CARD_TYPES.ERROR} />
          <TestableText className="mt-10">An error occurred while fetching the organisation overview data.</TestableText>
          <TestableText className="mt-5">Please try again later.</TestableText>
        </TestableCard>
        {switchDialog}
      </>
    );
  }

  let noProjects; // Var to hold the components to display when there are no projects, if needed

  if (filteredFlatProjects.length < 1 && !projectsLoading) {
    const createLink = ['super_admin', 'admin'].includes(userDetails?.user_type ?? '') ? ( // Only show the create project button if the user is an admin or super admin
      <div className="mt-5">
        <TestableButton
          dataTestId="org-overview-create-project-button"
          variant="outline"
          color="blue"
          onClick={() => {
            navigate('/admin_projects');
          }}
        >
          Create Project
        </TestableButton>
      </div>
    ) : (
      <TestableText dataTestId="org-overview-noproj-contact-admin">Please contact your administrator to create a project.</TestableText>
    );

    const actionMessage = filters.length > 0 ? 'Try adjusting your filters to see projects.' : createLink; // If there are filters applied, the org has projects but filters are hiding them.

    noProjects = (
      <div data-testid="org-overview-no-projects-card" className="p-4 mt-5 w-full text-center text-blue-500">
        <ErrorCard cardType={CARD_TYPES.EMPTY} />
        <TestableText variant="lead" className="mt-10">
          No projects found for this organisation.
        </TestableText>
        <TestableText className="mt-5">{actionMessage}</TestableText>
      </div>
    );
  }

  return (
    <>
      <TestableCard dataTestId="org-overview-card" className="mb-6">
        <div
          data-testid="org-overview-header"
          className="flex items-center justify-between mb-5 pb-2 border-b border-gray-400 gap-3"
        >
          <div className="min-w-0 flex items-center">
            <HiHome className="h-10 w-10 mr-3 shrink-0" />
            <TestableText dataTestId="org-overview-title" variant="h2" className="truncate">
              Overview - {organisationData?.title}
            </TestableText>
          </div>

          <div className="shrink-0 flex items-center gap-3">
            {ENABLE_AI_EXPLAINER && (
              <AIExplainerButton
                organisationName={organisationData?.title}
                projects={projectsWithTags}
              />
            )}
            <AvatarLogo
              alt={organisationData?.title}
              size="sm"
              displayType="logo"
              src={organisationData?.logo_url}
            />
          </div>
        </div>

        <div className="space-y-6">
          <OrganisationOverviewKpiSection
            projects={filteredFlatProjects}
            orgKPIsByProject={orgOverview?.kpis}
            isLoading={orgOverviewLoading || projectsLoading}
          />

          {noProjects === undefined ? (
            <>
              <OrganisationOverviewHealthMatrix projects={filteredFlatProjects} matrix={orgOverview?.matrix} isLoading={orgOverviewLoading} />
              <OrganisationOverviewProjectTable projects={filteredFlatProjects} isLoading={orgOverviewLoading} />
            </>
          ) : (
            noProjects
          )}
        </div>
      </TestableCard>
      {switchDialog}
    </>
  );
}

OrganisationOverviewContent.propTypes = {
  openExplainer: PropTypes.bool,
};
